import { useState, useEffect, useCallback, useRef } from 'react';
import { startInstance, createCheckout, cancelSubscription } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useInstances, getShortId } from '../hooks/useInstances';
import { useQuota } from '../hooks/useQuota';
import InstanceList from '../components/InstanceList';
import PackageSelectModal from '../components/PackageSelectModal';
import Toast from '../components/Toast';
import './DashboardPage.css';

// Fast poll state for action-triggered quick status updates
interface FastPollState {
  instanceId: string;
  targetStatus: 'running' | 'suspended';
  startedAt: number;
}

export default function DashboardPage() {
  const supportUrl = 'https://x.com/coldestlin';
  const [error, setError] = useState<string | null>(null);
  const [checkoutInProgress, setCheckoutInProgress] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showCreatingToast, setShowCreatingToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [fastPoll, setFastPoll] = useState<FastPollState | null>(null);
  const { user, session } = useAuth();

  // Track if refresh cookie has been set for current session
  const refreshCookieSetRef = useRef<string | null>(null);
  // Ref to track current instances for fast poll (avoids dependency loop)
  const instancesRef = useRef<typeof instances>([]);
  // Ref to track if a refresh request is in flight (prevents request stacking)
  const isRefreshingRef = useRef(false);
  const {
    instances,
    currentInstance: instance,
    hasInstances: hasInstance,
    loading: instanceLoading,
    error: instanceError,
    maxInstances,
    isAtLimit,
    selectInstance,
    deleteInstance,
    refresh: refreshInstance,
  } = useInstances();

  // Keep instancesRef in sync with instances (for fast poll without dependency loop)
  useEffect(() => {
    instancesRef.current = instances;
  }, [instances]);

  // Quota hook for billing info
  const {
    quota,
    subscription,
    loading: quotaLoading,
    isQuotaExhausted,
    isQuotaLow,
    runtimeUsedPercent,
    remainingTimeFormatted,
    hasSubscription,
    refresh: refreshQuota,
  } = useQuota(instance?.id || null);

  // Build chat URL
  const apiUrl = import.meta.env.VITE_API_URL || '';
  const baseDomain = import.meta.env.VITE_BASE_DOMAIN || 'openclaw.ski';
  const instanceId = instance?.id;
  const shortId = instanceId ? getShortId(instanceId) : null;
  const isLocalDev = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');

  const getLocalDevPort = () => {
    try {
      const url = new URL(apiUrl);
      return url.port || '8787';
    } catch {
      return '8787';
    }
  };

  const chatUrl = (() => {
    if (!instance || !shortId) return null;

    const targetPath = `/chat?session=${encodeURIComponent('agent:main:main')}`;

    if (isLocalDev) {
      const port = getLocalDevPort();
      const instanceOrigin = `http://claw-${shortId}.localhost:${port}`;
      const token = session?.access_token;
      const refreshToken = session?.refresh_token;
      if (token) {
        // Dev mode: pass refresh_token to set cookie
        // token-refresh.ts in OpenClaw detects dev mode and skips auto-refresh,
        // avoiding race condition with Dashboard (Supabase refresh_tokens are one-time use)
        let authUrl = `${instanceOrigin}/_auth?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(targetPath)}`;
        if (refreshToken) {
          authUrl += `&refresh_token=${encodeURIComponent(refreshToken)}`;
        }
        return authUrl;
      }
      return `${instanceOrigin}${targetPath}`;
    }

    return `https://claw-${shortId}.${baseDomain}${targetPath}`;
  })();

  // Set auth cookie for cross-domain requests
  const setAuthCookie = useCallback(() => {
    if (!session?.access_token) return;

    const apiUrl = import.meta.env.VITE_API_URL || '';
    const isLocalhost = apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1');

    if (isLocalhost) {
      document.cookie = `oc_auth_token=${session.access_token}; path=/; max-age=3600; domain=localhost; SameSite=Lax`;
    } else {
      try {
        const url = new URL(apiUrl);
        const hostname = url.hostname;
        const parts = hostname.split('.');
        const parentDomain = parts.length >= 2
          ? '.' + parts.slice(-2).join('.')
          : hostname;
        document.cookie = `oc_auth_token=${session.access_token}; path=/; max-age=3600; domain=${parentDomain}; SameSite=None; Secure`;
      } catch {
        document.cookie = `oc_auth_token=${session.access_token}; path=/; max-age=3600; SameSite=None; Secure`;
      }
    }
  }, [session?.access_token]);

  // Set refresh_token cookie (only once per session)
  const setRefreshTokenCookie = useCallback(async () => {
    if (!session?.access_token || !session?.refresh_token) return;

    // Skip if already set for this access_token
    if (refreshCookieSetRef.current === session.access_token) return;
    refreshCookieSetRef.current = session.access_token;

    const apiUrl = import.meta.env.VITE_API_URL || '';
    try {
      await fetch(`${apiUrl}/v1/auth/set-refresh-cookie`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ refreshToken: session.refresh_token }),
      });
    } catch (err) {
      console.error('[Dashboard] Failed to set refresh token cookie:', err);
      // Reset ref so it can retry on next render
      refreshCookieSetRef.current = null;
    }
  }, [session?.access_token, session?.refresh_token]);

  useEffect(() => {
    if (session?.access_token) {
      setAuthCookie();
      void setRefreshTokenCookie();
    }
  }, [session?.access_token, setAuthCookie, setRefreshTokenCookie]);

  // Start fast polling for a specific instance (triggered by user actions)
  const startFastPoll = useCallback((targetInstanceId: string, targetStatus: 'running' | 'suspended') => {
    setFastPoll({ instanceId: targetInstanceId, targetStatus, startedAt: Date.now() });
  }, []);

  const handleChatNavigation = useCallback((clickedInstanceId: string) => {
    setAuthCookie();
    // Trigger fast polling - the bot might be waking up
    startFastPoll(clickedInstanceId, 'running');
  }, [setAuthCookie, startFastPoll]);

  // Poll instances and quota
  useEffect(() => {
    if (user && hasInstance) {
      // Determine polling interval based on any instance in progress state
      const hasInProgressInstance = instances.some(inst =>
        inst.status === 'starting' || inst.status === 'creating' || inst.status === 'deleting'
      );
      const pollInterval = hasInProgressInstance ? 5000 : 15000;

      const doRefresh = async () => {
        // Skip if previous request still in flight
        if (isRefreshingRef.current) return;
        isRefreshingRef.current = true;
        try {
          await refreshInstance();
        } finally {
          isRefreshingRef.current = false;
        }
      };

      const instanceTimer = setInterval(doRefresh, pollInterval);
      const quotaTimer = setInterval(refreshQuota, 30000);

      return () => {
        clearInterval(instanceTimer);
        clearInterval(quotaTimer);
      };
    }
  }, [user, hasInstance, instances, refreshInstance, refreshQuota]);

  // Fast poll useEffect - triggered by user actions (Chat, Suspend)
  // Uses instancesRef to avoid dependency loop (instances update -> effect re-run -> refresh -> instances update)
  useEffect(() => {
    if (!fastPoll) return;

    const FAST_POLL_INITIAL_DELAY = 500; // 0.5 seconds for first poll
    const FAST_POLL_INTERVAL = 3000; // 3 seconds
    const FAST_POLL_DURATION = 180000; // 3 minutes

    const checkStatus = () => {
      // Use ref to get current instances without causing dependency loop
      const currentInstances = instancesRef.current;
      const instance = currentInstances.find(i => i.id === fastPoll.instanceId);
      const elapsed = Date.now() - fastPoll.startedAt;

      // Stop if target status reached or timeout
      if (instance?.status === fastPoll.targetStatus || elapsed >= FAST_POLL_DURATION) {
        setFastPoll(null);
        return true; // Signal to stop polling
      }
      return false; // Continue polling
    };

    const doPoll = async () => {
      // Skip if previous request still in flight (shared with regular polling)
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        await refreshInstance();
      } finally {
        isRefreshingRef.current = false;
      }
    };

    // Check immediately (status might already be correct)
    if (checkStatus()) return;

    let intervalTimer: ReturnType<typeof setInterval> | null = null;

    // First poll after 0.5s delay, then regular interval
    const initialTimer = setTimeout(async () => {
      await doPoll();
      if (checkStatus()) return;

      // Set up regular interval after first poll
      intervalTimer = setInterval(async () => {
        await doPoll();
        if (checkStatus() && intervalTimer) {
          clearInterval(intervalTimer);
        }
      }, FAST_POLL_INTERVAL);
    }, FAST_POLL_INITIAL_DELAY);

    return () => {
      clearTimeout(initialTimer);
      if (intervalTimer) clearInterval(intervalTimer);
    };
  }, [fastPoll, refreshInstance]);

  // Handle checkout - redirect to Stripe
  const handleCheckout = async (packageName: string, packageId: string, instanceName: string) => {
    setCheckoutInProgress(true);
    setError(null);
    try {
      const response = await createCheckout(packageId, instanceName);
      console.log(`[Dashboard] Checkout created for ${packageName}, redirecting to Stripe`);
      // Redirect to Stripe Checkout
      window.location.href = response.checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create checkout session');
      setCheckoutInProgress(false);
    }
  };

  // Handle URL parameters for checkout result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');

    if (checkoutStatus === 'success') {
      setToastMessage('Payment successful! Your agent is being created...');
      setShowCreatingToast(true);
      // Refresh instances to show the new one
      refreshInstance();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (checkoutStatus === 'canceled') {
      setToastMessage('Payment canceled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [refreshInstance]);

  const handleDeleteInstance = async (instanceId: string) => {
    setError(null);
    try {
      await deleteInstance(instanceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete instance');
    }
  };

  const handleStartInstance = async () => {
    if (!instanceId) return;
    setError(null);
    try {
      await startInstance(instanceId);
      await refreshInstance();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start instance');
    }
  };

  const handleCancelSubscription = async () => {
    if (!instanceId) return;
    if (!window.confirm('Are you sure you want to cancel your subscription? You can continue using the service until the end of the current billing period.')) {
      return;
    }

    setError(null);
    try {
      const result = await cancelSubscription(instanceId);
      setToastMessage(`Subscription canceled. Access continues until ${new Date(result.effectiveDate).toLocaleDateString()}`);
      setShowCreatingToast(true);
      await refreshQuota();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    }
  };

  return (
    <div className="dashboard-page">
      {(error || instanceError) && (
        <div className="error-banner">
          <span>{error || instanceError}</span>
          <button onClick={() => setError(null)} className="dismiss-btn">
            Dismiss
          </button>
        </div>
      )}

      {/* Loading state - only on initial load */}
      {instanceLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      )}

      {/* No instance state - show create button */}
      {!instanceLoading && !hasInstance && (
        <section className="welcome-section">
          <div className="welcome-content">
            <div className="welcome-emoji">🤖</div>
            <h2>Welcome to OpenClaw</h2>
            <p>Create your first AI agent to get started.</p>
            <button
              className="btn btn-primary btn-lg"
              onClick={() => setShowPackageModal(true)}
            >
              ✨ Create My Agent
            </button>
          </div>
        </section>
      )}

      {/* Has instance - show dashboard */}
      {!instanceLoading && hasInstance && (
        <>
          {/* Header with instance info and quota */}
          {instance && (
            <header className="dashboard-header">
              <div className="header-info">
                <h1>{instance.name}</h1>
                {quota && hasSubscription && (
                  <div className="quota-badge" title={`${remainingTimeFormatted} remaining`}>
                    <div
                      className="quota-bar"
                      style={{
                        width: `${100 - runtimeUsedPercent}%`,
                        backgroundColor: isQuotaExhausted
                          ? 'var(--error-color)'
                          : isQuotaLow
                            ? 'var(--warning-color)'
                            : 'var(--success-color)',
                      }}
                    />
                    <span className="quota-text">{remainingTimeFormatted}</span>
                  </div>
                )}
              </div>
            </header>
          )}

          {/* Quota warnings */}
          {isQuotaExhausted && (
            <div className="error-banner compact">
              <span>⚠️ Quota exhausted. Please upgrade your plan.</span>
            </div>
          )}
          {isQuotaLow && !isQuotaExhausted && (
            <div className="warning-banner compact">
              <span>⚠️ Low quota ({remainingTimeFormatted} left)</span>
            </div>
          )}
          {!quotaLoading && !hasSubscription && instance && (
            <div className="warning-banner compact">
              <span>⚠️ No active subscription</span>
            </div>
          )}

          {/* Subscription info */}
          {subscription && hasSubscription && (
            <div className="subscription-info">
              <div className="subscription-details">
                <span className="plan-name">{subscription.package?.name || 'Plan'}</span>
                <span className="plan-period">
                  Renews {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              {subscription.autoRenew && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </button>
              )}
              {!subscription.autoRenew && (
                <span className="canceled-badge">Cancels at period end</span>
              )}
            </div>
          )}

          {/* Instance Cards */}
          <InstanceList
            instances={instances}
            currentInstance={instance}
            isAtLimit={isAtLimit}
            maxInstances={maxInstances}
            chatUrl={chatUrl}
            onSelect={selectInstance}
            onCreateNew={() => setShowPackageModal(true)}
            onDelete={handleDeleteInstance}
            onStart={handleStartInstance}
            onChatNavigation={handleChatNavigation}
            onRefresh={refreshInstance}
            onStartFastPoll={startFastPoll}
            isQuotaExhausted={isQuotaExhausted}
          />
        </>
      )}

      <section className="dashboard-support">
        <span className="dashboard-support-text">Need help? DM us on X:</span>
        <a
          href={supportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="dashboard-support-link"
        >
          Contact Support
        </a>
      </section>

      {/* Package Selection Modal */}
      <PackageSelectModal
        isOpen={showPackageModal}
        onClose={() => setShowPackageModal(false)}
        onCheckout={handleCheckout}
        isProcessing={checkoutInProgress}
      />

      {/* Toast notifications */}
      {showCreatingToast && (
        <Toast
          message={toastMessage || "Agent is being created. This usually takes 2-3 minutes."}
          duration={5000}
          onClose={() => {
            setShowCreatingToast(false);
            setToastMessage(null);
          }}
        />
      )}
    </div>
  );
}
